import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import FlexxLogoImg from "../components/FlexxLogoImg";
import { WeeklySchedule, AdminScheduleEditor } from "./hub/ScheduleBoard";
import {
  T, TYPE_COLOR, TYPE_ICON, extractYouTubeId, extractVimeoId, formatDate,
  sectionTitle, card, btn, input, muteHint,
} from "./hub/hubShared";

const API = process.env.REACT_APP_BACKEND_URL;
const token = () => localStorage.getItem("flexx_access_token");
const authHeader = () => ({ headers: { Authorization: `Bearer ${token()}` } });

const CATEGORIES = ["FOREX", "CRYPTO", "INDICES", "AI TOOLS", "GENERAL"];
const FILTERS = [
  { k: "ALL", label: "All", color: T.gold },
  { k: "MUST_WATCH", label: "⭐ Must Watch", color: T.goldLight },
  { k: "BEGINNER", label: "🌱 For Beginners", color: T.emerald },
  { k: "FOREX", label: "Forex", color: TYPE_COLOR.FOREX },
  { k: "CRYPTO", label: "Crypto", color: TYPE_COLOR.CRYPTO },
  { k: "INDICES", label: "Indices", color: TYPE_COLOR.INDICES },
  { k: "AI TOOLS", label: "AI Tools", color: TYPE_COLOR["AI TOOLS"] },
];

function fileUrl(path) {
  return `${API}/api/hub/file/${path}?auth=${encodeURIComponent(token() || "")}`;
}

function thumbFor(v) {
  if (v.thumb_path) return fileUrl(v.thumb_path);
  if (v.source_type === "youtube") {
    const id = extractYouTubeId(v.external_url);
    if (id) return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
  }
  return null;
}

export default function AcademyHub() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [state, setState] = useState(null);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("ALL");
  const [tab, setTab] = useState("videos"); // videos | schedule
  const [player, setPlayer] = useState(null); // active video
  const [adminOpen, setAdminOpen] = useState(false);

  const loadVideos = async () => {
    try {
      const res = await axios.get(`${API}/api/hub/videos`, authHeader());
      setVideos(res.data.videos || []);
    } catch (e) { console.error("hub videos load failed", e); }
  };
  const loadState = async () => {
    try {
      const res = await axios.get(`${API}/api/live/state`, authHeader());
      setState(res.data);
    } catch (e) { console.error("live state load failed", e); }
  };

  useEffect(() => {
    (async () => { await Promise.all([loadVideos(), loadState()]); setLoading(false); })();
  }, []);

  const updateState = async (patch) => {
    const res = await axios.put(`${API}/api/live/state`, patch, authHeader());
    setState(res.data);
  };

  const filtered = useMemo(() => {
    if (filter === "ALL") return videos;
    if (filter === "MUST_WATCH") return videos.filter((v) => v.must_watch);
    if (filter === "BEGINNER") return videos.filter((v) => v.beginner);
    return videos.filter((v) => (v.category || "GENERAL") === filter);
  }, [videos, filter]);

  const mustWatch = videos.filter((v) => v.must_watch);
  const beginner = videos.filter((v) => v.beginner);

  if (loading) {
    return <div style={{ minHeight: "100vh", background: T.bg, color: T.mute, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", letterSpacing: 2 }}>LOADING ACADEMY HUB…</div>;
  }

  return (
    <div data-testid="academy-hub-page" style={{ minHeight: "100vh", background: T.bg, color: T.text, padding: "32px 20px 100px", fontFamily: "Inter, sans-serif" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <header style={{ marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <FlexxLogoImg style={{ height: 44, width: "auto", filter: `drop-shadow(0 0 14px ${T.gold}55)`, userSelect: "none" }} />
            <div style={{ height: 40, width: 1, background: T.border }} />
            <div>
              <div style={{ fontSize: 10, letterSpacing: 3, color: T.gold, fontFamily: "monospace", marginBottom: 4 }}>ACADEMY</div>
              <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: -0.5, fontFamily: "'Bebas Neue', 'Oswald', sans-serif", color: T.text }}>ACADEMY HUB</h1>
            </div>
          </div>
          {isAdmin && tab === "videos" && (
            <button data-testid="hub-admin-toggle-btn" onClick={() => setAdminOpen((s) => !s)}
              style={{ padding: "8px 14px", borderRadius: 6, background: "transparent", border: `1px solid ${T.borderHover}`, color: T.gold, cursor: "pointer", fontSize: 11, fontFamily: "monospace", letterSpacing: 1.5 }}>
              ⚙ MANAGE VIDEOS
            </button>
          )}
        </header>

        {/* Section tabs */}
        <div data-testid="hub-tabs" style={{ display: "flex", gap: 8, marginBottom: 22, borderBottom: `1px solid ${T.border}`, paddingBottom: 2 }}>
          {[{ k: "videos", label: "📼 Video Library" }, { k: "schedule", label: "📅 Training Schedule" }].map((t) => (
            <button key={t.k} data-testid={`hub-tab-${t.k}`} onClick={() => setTab(t.k)}
              style={{
                padding: "10px 16px", background: "transparent", border: "none", cursor: "pointer",
                color: tab === t.k ? T.gold : T.mute, fontFamily: "monospace", fontSize: 12, letterSpacing: 1, fontWeight: 700,
                borderBottom: `2px solid ${tab === t.k ? T.gold : "transparent"}`, marginBottom: -3,
              }}>{t.label}</button>
          ))}
        </div>

        {tab === "schedule" ? (
          <>
            {state && <WeeklySchedule schedule={state.schedule} overrides={state.overrides} />}
            {isAdmin && state && (
              <div style={card({ borderColor: T.gold + "60", background: T.goldGlow, marginBottom: 18 })}>
                <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 14 }}>ADMIN — EDIT TRAINING SCHEDULE</div>
                <AdminScheduleEditor state={state} onUpdate={updateState} />
              </div>
            )}
          </>
        ) : (
          <>
            {isAdmin && adminOpen && (
              <AdminVideoManager videos={videos} onChange={loadVideos} />
            )}

            {/* Highlight rails */}
            {filter === "ALL" && mustWatch.length > 0 && (
              <VideoRail testid="hub-rail-must-watch" title="⭐ MUST WATCH" accent={T.goldLight} videos={mustWatch} onPlay={setPlayer} />
            )}
            {filter === "ALL" && beginner.length > 0 && (
              <VideoRail testid="hub-rail-beginner" title="🌱 RECOMMENDED FOR BEGINNERS" accent={T.emerald} videos={beginner} onPlay={setPlayer} />
            )}

            {/* Filters */}
            <div data-testid="hub-filters" style={{ display: "flex", gap: 6, margin: "8px 0 18px", flexWrap: "wrap" }}>
              {FILTERS.map((f) => {
                const active = filter === f.k;
                return (
                  <button key={f.k} data-testid={`hub-filter-${f.k.toLowerCase().replace(/[ _]/g, "-")}`} onClick={() => setFilter(f.k)}
                    style={{ padding: "7px 14px", borderRadius: 999, fontSize: 11, fontFamily: "monospace", letterSpacing: 0.5, background: active ? f.color + "22" : "transparent", border: `1px solid ${active ? f.color : T.border}`, color: active ? f.color : T.mute, cursor: "pointer", fontWeight: 700 }}>
                    {f.label}
                  </button>
                );
              })}
            </div>

            <h2 style={sectionTitle}>{filter === "ALL" ? "📼 ALL VIDEOS" : `📼 ${FILTERS.find((f) => f.k === filter)?.label?.toUpperCase()}`}</h2>
            <div data-testid="hub-video-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 16 }}>
              {filtered.length === 0 && (
                <div style={{ ...card({}), gridColumn: "1 / -1", textAlign: "center", color: T.mute }}>
                  No videos in this category yet.{isAdmin ? " Click “Manage Videos” to add one." : ""}
                </div>
              )}
              {filtered.map((v) => <VideoCard key={v.id} v={v} onPlay={() => setPlayer(v)} />)}
            </div>
          </>
        )}
      </div>

      {player && <PlayerModal video={player} onClose={() => setPlayer(null)} />}
    </div>
  );
}

// ── Video rail (horizontal highlight strip) ─────────────────────────
function VideoRail({ title, accent, videos, onPlay, testid }) {
  return (
    <section data-testid={testid} style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 11, color: accent, fontFamily: "monospace", letterSpacing: 2, fontWeight: 800, marginBottom: 10 }}>{title}</div>
      <div style={{ display: "flex", gap: 14, overflowX: "auto", paddingBottom: 6 }}>
        {videos.map((v) => (
          <div key={v.id} style={{ minWidth: 240, maxWidth: 240 }}>
            <VideoCard v={v} onPlay={() => onPlay(v)} compact />
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Video card ──────────────────────────────────────────────────────
function VideoCard({ v, onPlay }) {
  const tColor = TYPE_COLOR[v.category] || T.gold;
  const thumb = thumbFor(v);
  const playable = !!(v.external_url || v.video_path);
  return (
    <div data-testid={`hub-video-card-${v.id}`} onClick={onPlay}
      style={{ ...card({ padding: 0, overflow: "hidden" }), cursor: "pointer", transition: "transform 0.15s, box-shadow 0.15s" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-3px)"; e.currentTarget.style.boxShadow = `0 8px 26px ${T.goldGlow}`; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "none"; }}>
      <div style={{ position: "relative", aspectRatio: "16 / 9", background: `linear-gradient(135deg, ${T.surface} 0%, ${T.panel} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
        {thumb ? (
          <img src={thumb} alt={v.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={(e) => { e.currentTarget.style.display = "none"; }} />
        ) : (
          <span style={{ color: tColor, fontSize: 46, opacity: 0.5 }}>{TYPE_ICON[v.category] || "📺"}</span>
        )}
        <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.15)" }}>
          <div style={{ width: 46, height: 46, borderRadius: 999, background: "rgba(0,0,0,0.55)", border: `1px solid ${(playable ? T.gold : T.mute)}88`, display: "flex", alignItems: "center", justifyContent: "center", color: playable ? T.gold : T.mute, fontSize: playable ? 18 : 16 }}>{playable ? "▶" : "⏳"}</div>
        </div>
        <div style={{ position: "absolute", top: 8, left: 8, display: "flex", gap: 5, flexWrap: "wrap" }}>
          {v.must_watch && <span style={badge(T.goldLight)}>⭐ MUST WATCH</span>}
          {v.beginner && <span style={badge(T.emerald)}>🌱 BEGINNER</span>}
        </div>
        {!playable && <span style={{ position: "absolute", bottom: 8, right: 8, ...badge(T.mute) }}>COMING SOON</span>}
        {playable && v.source_type === "file" && <span style={{ position: "absolute", bottom: 8, right: 8, ...badge(T.blue) }}>FILE</span>}
      </div>
      <div style={{ padding: 13 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 7, gap: 6 }}>
          <span style={{ fontSize: 9, padding: "3px 8px", borderRadius: 4, background: tColor + "22", color: tColor, fontFamily: "monospace", letterSpacing: 1, fontWeight: 700 }}>{v.category}</span>
          <span style={{ fontSize: 10, color: T.mute, fontFamily: "monospace" }}>
            {v.recorded_date ? formatDate(v.recorded_date) : ""}{v.recorded_date && v.duration_min ? " · " : ""}{v.duration_min ? `${v.duration_min}m` : ""}
          </span>
        </div>
        <div style={{ color: T.text, fontSize: 13.5, fontWeight: 600, lineHeight: 1.4, marginBottom: 5 }}>{v.title}</div>
        {v.description ? <div style={{ color: T.mute, fontSize: 11.5, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{v.description}</div> : null}
      </div>
    </div>
  );
}

const badge = (c) => ({ fontSize: 8.5, padding: "3px 7px", borderRadius: 4, background: "rgba(0,0,0,0.65)", color: c, fontFamily: "monospace", letterSpacing: 1, fontWeight: 800, border: `1px solid ${c}66` });

// ── Universal player modal ──────────────────────────────────────────
function PlayerModal({ video, onClose }) {
  const ytId = video.source_type === "youtube" ? extractYouTubeId(video.external_url) : "";
  const vimeoId = video.source_type === "vimeo" ? extractVimeoId(video.external_url) : "";
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div data-testid="hub-player-modal" onClick={onClose}
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(0,0,0,0.85)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "100%", maxWidth: 960, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${T.border}` }}>
          <div style={{ color: T.text, fontSize: 14, fontWeight: 700 }}>{video.title}</div>
          <button data-testid="hub-player-close" onClick={onClose} style={{ background: "transparent", border: "none", color: T.mute, fontSize: 22, cursor: "pointer", lineHeight: 1 }}>×</button>
        </div>
        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, background: "#000" }}>
          {ytId ? (
            <iframe data-testid="hub-player-youtube" src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`} title={video.title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
          ) : vimeoId ? (
            <iframe data-testid="hub-player-vimeo" src={`https://player.vimeo.com/video/${vimeoId}?autoplay=1`} title={video.title}
              allow="autoplay; fullscreen; picture-in-picture" allowFullScreen
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }} />
          ) : video.video_path ? (
            <video data-testid="hub-player-file" src={fileUrl(video.video_path)} controls autoPlay
              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", background: "#000" }} />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", gap: 8, alignItems: "center", justifyContent: "center", color: T.mute, textAlign: "center", padding: 20 }}>
              <span style={{ fontSize: 30 }}>⏳</span>
              <div style={{ fontSize: 13 }}>This recording will be posted here soon.</div>
            </div>
          )}
        </div>
        {video.description ? <div style={{ padding: 16, color: T.mute, fontSize: 13, lineHeight: 1.6, maxHeight: 160, overflowY: "auto" }}>{video.description}</div> : null}
      </div>
    </div>
  );
}

// ── Admin video manager ─────────────────────────────────────────────
const EMPTY_FORM = { title: "", description: "", category: "GENERAL", source_type: "youtube", external_url: "", video_path: "", thumb_path: "", must_watch: false, beginner: false, duration_min: 0, recorded_date: "" };

function AdminVideoManager({ videos, onChange }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState({ video: 0, thumb: 0 });
  const videoInputRef = useRef(null);
  const thumbInputRef = useRef(null);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ ...EMPTY_FORM, ...v });
    setErr("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditingId(null); setForm(EMPTY_FORM); setErr(""); };

  const uploadFile = async (kind, file) => {
    if (!file) return;
    setErr("");
    const fd = new FormData();
    fd.append("file", file);
    setUploading((u) => ({ ...u, [kind]: 1 }));
    try {
      const res = await axios.post(`${API}/api/hub/upload?kind=${kind}`, fd, {
        headers: { Authorization: `Bearer ${token()}` },
        onUploadProgress: (e) => { if (e.total) setUploading((u) => ({ ...u, [kind]: Math.round((e.loaded / e.total) * 100) })); },
      });
      if (kind === "video") set({ video_path: res.data.path, source_type: "file" });
      else set({ thumb_path: res.data.path });
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading((u) => ({ ...u, [kind]: 0 }));
    }
  };

  const save = async () => {
    setErr("");
    if (!form.title.trim()) { setErr("Title is required."); return; }
    if (form.source_type === "file" && !form.video_path) { setErr("Upload a video file first."); return; }
    if (form.source_type !== "file" && !form.external_url.trim()) { setErr("Paste a YouTube/Vimeo link."); return; }
    setBusy(true);
    try {
      const payload = { ...form, duration_min: Number(form.duration_min) || 0 };
      if (editingId) await axios.put(`${API}/api/hub/videos/${editingId}`, payload, authHeader());
      else await axios.post(`${API}/api/hub/videos`, payload, authHeader());
      cancelEdit();
      await onChange();
    } catch (e) {
      setErr(e.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await axios.delete(`${API}/api/hub/videos/${id}`, authHeader()); await onChange(); }
    catch (e) { setErr(e.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div data-testid="hub-admin-video-manager" style={card({ borderColor: T.gold + "60", background: T.goldGlow, marginBottom: 20 })}>
      <div style={{ fontSize: 10, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 14 }}>
        {editingId ? "ADMIN — EDIT VIDEO" : "ADMIN — ADD VIDEO"}
      </div>

      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <div>
            <div style={fieldLabel}>TITLE</div>
            <input data-testid="hub-form-title" value={form.title} onChange={(e) => set({ title: e.target.value })} placeholder="Video title" style={{ ...input, width: "100%" }} />
          </div>
          <div>
            <div style={fieldLabel}>CATEGORY</div>
            <select data-testid="hub-form-category" value={form.category} onChange={(e) => set({ category: e.target.value })} style={{ ...input, width: "100%", padding: "8px 8px" }}>
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <div style={fieldLabel}>DURATION (MIN)</div>
            <input data-testid="hub-form-duration" type="number" min="0" value={form.duration_min} onChange={(e) => set({ duration_min: e.target.value })} style={{ ...input, width: "100%" }} />
          </div>
          <div>
            <div style={fieldLabel}>RECORDED DATE</div>
            <input data-testid="hub-form-date" type="date" value={form.recorded_date || ""} onChange={(e) => set({ recorded_date: e.target.value })} style={{ ...input, width: "100%" }} />
          </div>
        </div>

        <div>
          <div style={fieldLabel}>DESCRIPTION</div>
          <textarea data-testid="hub-form-description" value={form.description} onChange={(e) => set({ description: e.target.value })} placeholder="What will members learn?" rows={2} style={{ ...input, width: "100%", resize: "vertical", fontFamily: "inherit" }} />
        </div>

        {/* Source selector */}
        <div>
          <div style={fieldLabel}>VIDEO SOURCE</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
            {[{ k: "youtube", l: "YouTube" }, { k: "vimeo", l: "Vimeo" }, { k: "file", l: "Upload file" }].map((s) => (
              <button key={s.k} data-testid={`hub-form-source-${s.k}`} onClick={() => set({ source_type: s.k })} style={btn({ active: form.source_type === s.k, color: T.gold })}>{s.l}</button>
            ))}
          </div>
          {form.source_type === "file" ? (
            <div>
              <input ref={videoInputRef} type="file" accept="video/*" data-testid="hub-form-video-file" style={{ display: "none" }} onChange={(e) => uploadFile("video", e.target.files[0])} />
              <button onClick={() => videoInputRef.current?.click()} style={btn({ active: !!form.video_path, color: form.video_path ? T.emerald : T.gold })}>
                {uploading.video ? `Uploading… ${uploading.video}%` : form.video_path ? "✓ Video uploaded — replace" : "⬆ Upload video file"}
              </button>
              <div style={muteHint}>MP4/WebM/MOV up to 200MB. For long recordings, YouTube/Vimeo is recommended.</div>
            </div>
          ) : (
            <input data-testid="hub-form-external-url" value={form.external_url} onChange={(e) => set({ external_url: e.target.value })} placeholder={form.source_type === "vimeo" ? "https://vimeo.com/123456789" : "https://youtu.be/…"} style={{ ...input, width: "100%" }} />
          )}
        </div>

        {/* Thumbnail */}
        <div>
          <div style={fieldLabel}>CUSTOM THUMBNAIL (OPTIONAL)</div>
          <input ref={thumbInputRef} type="file" accept="image/*" data-testid="hub-form-thumb-file" style={{ display: "none" }} onChange={(e) => uploadFile("thumb", e.target.files[0])} />
          <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <button onClick={() => thumbInputRef.current?.click()} style={btn({ active: !!form.thumb_path, color: form.thumb_path ? T.emerald : T.gold })}>
              {uploading.thumb ? `Uploading… ${uploading.thumb}%` : form.thumb_path ? "✓ Thumbnail set — replace" : "⬆ Upload thumbnail"}
            </button>
            {form.thumb_path && <img alt="thumb" src={fileUrl(form.thumb_path)} style={{ height: 44, borderRadius: 6, border: `1px solid ${T.border}` }} />}
            {form.thumb_path && <button onClick={() => set({ thumb_path: "" })} style={btn({ active: false, color: T.mute })}>Remove</button>}
          </div>
        </div>

        {/* Tags */}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap" }}>
          <label style={tagLabel}>
            <input data-testid="hub-form-must-watch" type="checkbox" checked={form.must_watch} onChange={(e) => set({ must_watch: e.target.checked })} style={{ accentColor: T.gold, width: 16, height: 16 }} />
            <span style={{ color: form.must_watch ? T.goldLight : T.mute }}>⭐ Must Watch</span>
          </label>
          <label style={tagLabel}>
            <input data-testid="hub-form-beginner" type="checkbox" checked={form.beginner} onChange={(e) => set({ beginner: e.target.checked })} style={{ accentColor: T.emerald, width: 16, height: 16 }} />
            <span style={{ color: form.beginner ? T.emerald : T.mute }}>🌱 Recommended for Beginners</span>
          </label>
        </div>

        {err && <div data-testid="hub-form-error" style={{ color: T.red, fontSize: 12, fontFamily: "monospace" }}>{err}</div>}

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          {editingId && <button onClick={cancelEdit} style={btn({ active: false, color: T.mute })}>Cancel</button>}
          <button data-testid="hub-form-save" onClick={save} disabled={busy} style={btn({ active: true, color: T.gold })}>{busy ? "Saving…" : editingId ? "Update Video" : "Add Video"}</button>
        </div>
      </div>

      {/* Existing videos list */}
      <div style={{ marginTop: 18, borderTop: `1px solid ${T.border}`, paddingTop: 14 }}>
        <div style={{ fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 10 }}>LIBRARY ({videos.length})</div>
        <div style={{ display: "grid", gap: 8, maxHeight: 320, overflowY: "auto", paddingRight: 4 }}>
          {videos.map((v) => (
            <div key={v.id} data-testid={`hub-admin-row-${v.id}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, padding: 10, background: T.panel, border: `1px solid ${T.border}`, borderRadius: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: T.text, fontSize: 13, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{v.title}</div>
                <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace", marginTop: 3 }}>
                  {v.category} · {v.source_type}{v.must_watch ? " · ⭐" : ""}{v.beginner ? " · 🌱" : ""}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <button data-testid={`hub-admin-edit-${v.id}`} onClick={() => startEdit(v)} style={btn({ active: false, color: T.gold })}>Edit</button>
                <button data-testid={`hub-admin-delete-${v.id}`} onClick={() => remove(v.id)} style={btn({ active: false, color: T.red })}>Delete</button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const fieldLabel = { fontSize: 9, letterSpacing: 2, color: T.gold, fontFamily: "monospace", marginBottom: 6 };
const tagLabel = { display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, fontFamily: "monospace", fontWeight: 700, userSelect: "none" };
